import Head from "next/head"

import {GetStaticProps, GetStaticPaths, NextPage} from "next"
import {getAllCountryPaths, getCountryData} from "../../../src/data/content";
import {MDXRemote, MDXRemoteSerializeResult} from "next-mdx-remote";
import {Title} from "../../../src/components/remark/title";
import {Intro} from "../../../src/components/remark/intro";
import {OfferLink} from "../../../src/components/remark/offer-link";
import {Youtube} from "../../../src/components/remark/youtube";
import {Img} from "../../../src/components/remark/img";
import {Meters} from "../../../src/components/remark/meters";
import {HiddenMarkup} from "../../../src/components/remark/hidden-markup";
import {Temp} from "../../../src/components/remark/temp";
import {VisaRequirements} from "../../../src/components/remark/visa-requirements";
import {MainLayout} from "../../../src/components/main-layout";
import {Container, Grid, Paper, Box, Breadcrumbs, Typography} from "@mui/material";
import {serverSideTranslations} from "next-i18next/serverSideTranslations";
import {Forex} from "../../../src/components/remark/forex";
import React, {useRef, useEffect} from "react";
import {useTranslation} from "next-i18next";
import Image from "next/legacy/image";
import {H1, H2, H3, H4, H5, H6} from "../../../src/components/remark/headings/markdown-heading";
import {A} from "../../../src/components/remark/a";
import {setCriteriaContinent} from "../../../src/slices/trips-slice";
import {useRouter} from "next-translate-routes/router";
import googleBucketLoader from "../../../src/lib/google-bucket-loader";
import {fileUrlToUrl, urlToFileUrl} from "next-translate-routes"

import {TripDb} from "seacrush-core"
import _tripDatabase from "../../../src/generated/trip-database.json";
import {RecommendedTripAd} from "../../../src/components/recommended-trip-ad";
import {tripResultsApi} from "../../../src/frontend-api/trip-api";
import {TripInterface} from "../../../src/types/trip";
import useWidth from "../../../src/hooks/use-width";
import {NextSeo, BreadcrumbJsonLd} from "next-seo";
import countryDataJson from "../../../src/generated/country-data.json"

import {Link} from "../../../src/components/link"
import {WhatsApp} from "../../../src/components/whatsapp"
import {useDispatch} from "../../../src/store"

const tripDatabase = _tripDatabase as TripDb

interface CountryPageProps {
    recommendedTrips: TripInterface[],
    markdownData: {
        frontmatter: Record<string, string>;
        subDir: string;
        overviewSource: MDXRemoteSerializeResult;
        tocSource: MDXRemoteSerializeResult;
        source: MDXRemoteSerializeResult;
    }
}

const initialComponents = {
    Title,
    Intro,
    OfferLink,
    Youtube,
    Overview: HiddenMarkup,
    Temp,
    VisaRequirements,
    Meters,
    Forex,
    h1: H3,
    h2: (props: any) => <H4 sx={{color: "primary.dark", textDecoration: "underline"}} {...props}/>,
    h3: H5,
    h4: H6,
    h5: H5,
    h6: H6,
    li: (props: any) => <Box component={"li"}
                             sx={{listStyle: "none", paddingInlineStart: 1, marginTop: 1}} {...props}/>,
    ul: (props: any) => <Box component={"ul"} sx={{listStyle: "none", paddingInlineStart: 2}} {...props}/>,
    a: A

}

const CountryPage: NextPage<CountryPageProps> = ({
                                                     recommendedTrips,
                                                     markdownData: {
                                                         frontmatter,
                                                         subDir,
                                                         source,
                                                         tocSource,
                                                         overviewSource
                                                     }
                                                 }) => {

    const {t, i18n} = useTranslation()

    const dispatch = useDispatch()

    const router = useRouter()

    const {pathname, locales, locale, query} = router

    const components = {
        img: (props: any) => {
            return <Img
                {...props}
                src={`/content/countries/${subDir}/${props.src}`}/>
        },
        ...initialComponents
    }

    const isMaxWidth = useWidth() === "xs"



    const stickyElement = useRef(null);

    useEffect(() => {

        if (!(typeof window === "undefined")) {
            // @ts-ignore
            stickyElement.current?.setAttribute(
                "style",
                "position: -webkit-sticky; position: sticky; top: 80px; overflow: auto"
            );
        }
    }, []);


    return (
        <div>
            <Head>
                <title>{frontmatter.title + " | SeaCrush"}</title>
            </Head>
            <NextSeo
                facebook={{
                    appId: process.env.NEXT_PUBLIC_FACEBOOK_APP_ID as string
                }}
                openGraph={{
                    url: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}${fileUrlToUrl({
                        pathname: router.pathname,
                        query: router.query
                    }, router.locale || "en")}`,
                    type: "website",
                    title: frontmatter.title,
                    description: frontmatter.description,
                    images: [
                        {
                            width: 640,
                            height: 334,
                            type: "image/webp",
                            url: `${process.env.NEXT_PUBLIC_IMAGE_ROOT}/content/countries/${subDir}/best_dive_sites_in_${subDir}.${locale || "en"}_640__.webp`
                        },
                        {
                            width: 1920,
                            height: 1003,
                            type: "image/webp",
                            url: `${process.env.NEXT_PUBLIC_IMAGE_ROOT}/content/countries/${subDir}/best_dive_sites_in_${subDir}.${locale || "en"}_1920__.webp`
                        }
                    ]
                }}
                twitter={{
                    cardType: "summary_large_image"
                }}
                languageAlternates={
                    locales?.filter((l) => l !== locale && l !== "default").map(
                        (l) => {
                            return {
                                hrefLang: l,
                                href: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}` + fileUrlToUrl({
                                        pathname,
                                        query: {
                                            //@ts-ignore
                                            countrySlug: countryDataJson.filter((el:any) => el.slug.en.current === subDir)[0].slug[l].current
                                        }
                                    },
                                    l)
                            }
                        }
                    )
                }
            />
            <section>
                <Container maxWidth='lg' sx={{
                    minHeight: 300,
                    mt: {
                        xs: 3,
                        sm: 0
                    },
                    px: {
                        xs: 0,
                        sm: 1
                    },
                    pb: 3
                }}>

                    <BreadcrumbJsonLd
                        itemListElements={[
                            {
                                position: 1,
                                name: t("diving"),
                                item: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}${fileUrlToUrl("/diving", i18n.language)}`,
                            },
                            {
                                position: 2,
                                name: frontmatter.continentName,
                                item: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}${fileUrlToUrl("/diving", i18n.language)}/${frontmatter.continentName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`,
                            },
                            {
                                position: 3,
                                name: frontmatter.countryName,
                                item: `https://${process.env.NEXT_PUBLIC_SEACRUSH_HOST}${fileUrlToUrl({
                                    pathname: router.pathname,
                                    query: router.query
                                }, router.locale || "en")}`,
                            }
                        ]}
                    />
                    <Breadcrumbs aria-label='breadcrumb' sx={{
                        py: 2, px: {
                            xs: 1,
                            sm: 0
                        }
                    }}>
                        <Link href={"/diving"}>
                            {t("diving")}
                        </Link>
                        <Link
                            href={"/diving"}
                            as={`${fileUrlToUrl("/diving", i18n.language)}/${frontmatter.continentName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")}`}
                            onClick={(e) => {
                                // e.preventDefault()
                                dispatch(setCriteriaContinent(frontmatter.continentId))
                                // const selectedContinentSlug = frontmatter.continentName.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "")
                                // router.push('/diving', `/diving/${selectedContinentSlug}`, {unstable_skipClientCache: true, shallow: true} )
                            }}
                        >
                            {frontmatter.continentName}
                        </Link>
                        <span>
                            {frontmatter.countryName}
                        </span>
                    </Breadcrumbs>

                    <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                            <Paper elevation={3}>

                                <Image
                                    src={`/content/countries/${subDir}/best_dive_sites_in_${subDir}.${i18n.language}.webp`}
                                    // src={"/content/countries/costa-rica/best_dive_sites_in_costa-rica.en.png"}
                                    placeholder={"blur"}
                                    blurDataURL={"data:image/webp;base64,UklGRlYAAABXRUJQVlA4IEoAAADwAQCdASoQAAgAAUAmJZQAAxeIrFki8AAA/vmoEyaqa08yx4/s80PtyFEqaYJFZz/Di3gH8jKXYgkY3WiHyCv3W8ctW63GcwAAAA=="}
                                    width={1520}
                                    height={794}
                                    alt={frontmatter.title}
                                    loader={googleBucketLoader}
                                    sizes={"50vw"}
                                />

                                <Box sx={{
                                    p: {
                                        xs: 2,
                                        md: 4
                                    }
                                }}>
                                    <Typography variant={"h1"}>{frontmatter.overviewTitle}</Typography>

                                    <MDXRemote {...overviewSource} components={components}/>

                                    <Box component={"nav"}
                                         className={"tableOfCountentsBox"}
                                         sx={{my: 6, backgroundColor: "#f6f6f6", border: "2px solid #3895b2"}}>
                                        <MDXRemote {...tocSource} components={components}/>
                                    </Box>

                                    <MDXRemote {...source} components={components}/>
                                </Box>
                            </Paper>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <div ref={stickyElement}>
                                <div style={{height: isMaxWidth? "unset" : "90vh"}}>
                                    {recommendedTrips?.map((trip) => {
                                        return <RecommendedTripAd key={trip.tripId} diveTrip={trip}/>
                                    })}
                                </div>
                            </div>

                        </Grid>
                    </Grid>


                </Container>
                <WhatsApp/>
            </section>
        </div>
    );
}


CountryPage.getLayout = (page) => (
    <MainLayout>
        {page}
    </MainLayout>
);

export default CountryPage

export const getStaticPaths: GetStaticPaths = async (context) => {
    const locales = context.locales?.filter((val) => val !== "default") || []
    const paths = await getAllCountryPaths(locales)

    return {
        paths,
        fallback: false
    }
}

export const getStaticProps: GetStaticProps = async ({params, locale}) => {
    if (locale === "default" || locale === undefined) {
        return {notFound: true};
    }

    const markdownData = await getCountryData(params?.countrySlug as string, locale)

    const recommendedTrips = (await tripResultsApi.getTrips(locale)).filter((trip) => trip.country.slug === params?.countrySlug) ?? null;


    return {
        props: {
            markdownData,
            recommendedTrips,
            ...(await serverSideTranslations(locale as string, ["frontend"])),
        }
    };
}

